import { Grid } from '@farewill/ui'
import { useFormikContext } from 'formik'

import InputFloating from 'components/form/input-floating'
import { YES_NO_OPTIONS } from 'utils/enums'

import withForm from 'lib/formik/with-form'
import useValidatedSave from 'lib/formik/use-validated-save'
import { SHARED_PROPERTY_SCHEMA } from 'lib/probate/estate-questionnaire-schema'
import {
  ProbateEstateProperty,
  ProbateEstatePropertiesAttributes,
} from 'lib/models/probate-estate'
import { Address as AddressType } from 'lib/models/address'

import { FieldsGrid } from '../../components/collection-section'
import Address from '../../components/address'
import NotesField from '../../components/notes-field'
import RadioGroup from '../../components/radio-group'
import {
  JOINT_OWNERSHIP_TYPE_OPTIONS,
  OWNERSHIP_OPTIONS,
  PROPERTY_TYPE_OPTIONS,
  TENURE_OPTIONS,
  YES_NO_DONT_KNOW_OPTIONS,
} from '../../options'
import CoOwners from './co-owners'

// the joint details can be cleared, which the model's types don't allow
export type PropertyUpdate = Partial<
  Omit<
    ProbateEstatePropertiesAttributes,
    'jointOwnershipType' | 'deceasedShareOfJointOwnership'
  >
> & {
  jointOwnershipType?: string | null
  deceasedShareOfJointOwnership?: string | null
}

type UpdateProperty = (id: number, attributes: PropertyUpdate) => unknown

type RowProps = {
  property: ProbateEstateProperty
  isMainResidence: boolean
  updateProperty: UpdateProperty
}

const PropertyFields = ({
  property,
  isMainResidence,
  updateProperty,
}: RowProps) => {
  const { values, setFieldValue } = useFormikContext<Record<string, unknown>>()

  const handleSave = useValidatedSave({
    schema: SHARED_PROPERTY_SCHEMA,
    save: ({ name, value }) => updateProperty(property.id, { [name]: value }),
  })

  // for the saves that don't go through handleSave - as there, the
  // collection hook shows a failure's error, so it's left there rather
  // than going unhandled
  const saveProperty = (attributes: PropertyUpdate) =>
    Promise.resolve(updateProperty(property.id, attributes)).catch(
      () => undefined
    )

  // the joint details only mean something while the property is jointly
  // owned, so they're cleared in the same save as the switch away from it
  const handleOwnershipSave: typeof handleSave = async ({ name, value }) => {
    if (value === 'joint') return handleSave({ name, value })

    setFieldValue('jointOwnershipType', '')
    setFieldValue('deceasedShareOfJointOwnership', '')
    // the co-owners stay on the estate as contacts
    saveProperty({
      ownership: value as string,
      jointOwnershipType: null,
      deceasedShareOfJointOwnership: null,
      coOwnerIds: [],
    })
  }

  return (
    <>
      <FieldsGrid gap="S">
        <Grid.Item span={12}>
          <Address
            label="Property address"
            name="address"
            data={(values.address as AddressType) ?? null}
          />
        </Grid.Item>
        {!isMainResidence && (
          <Grid.Item span={6}>
            <InputFloating
              name="propertyType"
              label="Property type"
              component="select"
              options={PROPERTY_TYPE_OPTIONS}
              handleSave={handleSave}
            />
          </Grid.Item>
        )}
        <Grid.Item span={12}>
          <Grid gap="S">
            <Grid.Item span={6}>
              <InputFloating
                name="value"
                label="Value (£)"
                isCurrency
                handleSave={handleSave}
              />
            </Grid.Item>
            <Grid.Item span={6}>
              <RadioGroup
                label="Is this an estimate?"
                name="isEstimatedValue"
                options={YES_NO_OPTIONS}
                handleSave={handleSave}
                inline
              />
            </Grid.Item>
          </Grid>
        </Grid.Item>
        <Grid.Item span={12}>
          <RadioGroup
            label="Ownership"
            name="ownership"
            options={OWNERSHIP_OPTIONS}
            handleSave={handleOwnershipSave}
            inline
          />
        </Grid.Item>
        {values.ownership === 'joint' && (
          <>
            <Grid.Item span={12}>
              <RadioGroup
                label="Joint ownership type"
                name="jointOwnershipType"
                options={JOINT_OWNERSHIP_TYPE_OPTIONS}
                handleSave={handleSave}
                inline
              />
            </Grid.Item>
            <Grid.Item span={6}>
              <InputFloating
                name="deceasedShareOfJointOwnership"
                label="Share owned by the deceased (%)"
                handleSave={handleSave}
              />
            </Grid.Item>
            <CoOwners
              coOwnerIds={property.attributes.coOwnerIds ?? []}
              saveCoOwnerIds={async (coOwnerIds) => {
                await updateProperty(property.id, { coOwnerIds })
              }}
            />
          </>
        )}
        <Grid.Item span={12}>
          <RadioGroup
            label="Tenure"
            name="tenure"
            options={TENURE_OPTIONS}
            handleSave={handleSave}
            inline
          />
        </Grid.Item>
        <Grid.Item span={12}>
          <RadioGroup
            label="Is the property currently occupied?"
            name="isCurrentlyOccupied"
            options={YES_NO_OPTIONS}
            handleSave={handleSave}
            inline
          />
        </Grid.Item>
        <Grid.Item span={12}>
          <RadioGroup
            label="Is the property currently insured?"
            name="isInsured"
            options={YES_NO_DONT_KNOW_OPTIONS}
            handleSave={handleSave}
            inline
          />
        </Grid.Item>
        {values.isInsured === 'yes' && (
          <>
            <Grid.Item span={6}>
              <InputFloating
                name="insuranceProviderName"
                label="Insurance provider"
                handleSave={handleSave}
              />
            </Grid.Item>
            <Grid.Item span={6}>
              <InputFloating
                name="insurancePolicyNumber"
                label="Policy number"
                handleSave={handleSave}
              />
            </Grid.Item>
          </>
        )}
      </FieldsGrid>
      <NotesField name="notes" handleSave={handleSave} />
    </>
  )
}

const PropertyForm = withForm(PropertyFields)

const Property = ({ property, isMainResidence, updateProperty }: RowProps) => (
  <PropertyForm
    initialValues={{
      ...property.attributes,
      ownership: property.attributes.ownership || 'sole',
    }}
    validationSchema={SHARED_PROPERTY_SCHEMA}
    property={property}
    isMainResidence={isMainResidence}
    updateProperty={updateProperty}
  />
)

export default Property
